import React, { useEffect, useState } from "react";

import { useForm } from "react-hook-form/dist/react-hook-form.ie11";
import { useHistory } from "react-router-dom";

import {
  Address,
  FranchiseType,
  LearnerLessonType,
  Location,
  UserType,
} from "@hubcapp/types";
import { fireOnce } from "@ds-developer/frontend";
import {
  learnerLessonTypeToPersonType,
  sortPropositionAllocationsForTabs,
} from "@hubcapp/utils";
import { useAuth, useTealium } from "@hubcapp/context";
import { useQueryGetInstructorPropositionDetails } from "@hubcapp/hooks";

import { NewLearnerFormFields } from "./types";
import {
  instructorPricesToLessonPrices,
  isValidPrice,
  propositionsToFranchiseOptions,
} from "./utils";
import {
  useMutationCreateSelfSourcedLearner,
  useQueryEmailAddressExistsCheck,
  useQueryGetFullAddress,
  useQueryGetPostcodeInfo,
} from "./hooks";
import { useSnackbar } from "notistack";
import NewLearner from "./NewLearner";
import validationSchema from "./validationSchema";

const NewLearnerStateProvider: React.FC = () => {
  const {
    instructor: { id: instructorId },
  } = useAuth();

  const history = useHistory();
  const { trackEvent } = useTealium();
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: instructorDetailsData,
    loading: instructorDetailsLoading,
  } = useQueryGetInstructorPropositionDetails(instructorId);

  const franchises = propositionsToFranchiseOptions(
    sortPropositionAllocationsForTabs(
      instructorDetailsData?.instructor?.propositions || [],
    ),
  ).filter(
    fran =>
      fran.type === FranchiseType.Full ||
      fran.type === FranchiseType.PartnerLite ||
      fran.type === FranchiseType.Trainee,
  );

  const defaultFranchiseId =
    franchises && franchises.length === 1 ? franchises[0]?.id : "";

  const {
    errors,
    formState: { touched, dirty },
    handleSubmit,
    register,
    setValue,
    triggerValidation,
    setError,
    watch,
  } = useForm<NewLearnerFormFields>({
    defaultValues: { address: "", franchiseId: defaultFranchiseId },
    mode: "onBlur",
    reValidateMode: "onChange",
    validationSchema,
  });
  // These are controlled components so they need to be manually registered in this way
  useEffect(() => {
    register({ name: "franchiseId" });
    register({ name: "salutation" });
    register({ name: "address" });
    register({ name: "mobileNumber" });
    register({ name: "dateOfBirth" });
    register({ name: "lessonType" });
    register({ name: "lessonPrice" });
  }, []);

  const [franchiseId, setFranchiseId] = useState<string>(defaultFranchiseId);
  const [lessonType, setLessonType] = useState<LearnerLessonType | undefined>();
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");
  const [lessonPrice, setLessonPrice] = useState<string | undefined>();

  const onCompleted = (data: any) => {
    const createdLearner = data?.createSelfSourcedLearner;
    if (createdLearner?.id) {
      history.push(`/learners/${createdLearner.id}`);
    }
  };

  const onError = () => {
    enqueueSnackbar("Unable to create learner", {
      variant: "error",
      role: "alert",
      autoHideDuration: 2000,
    });
  };

  const [
    createLearnerMutation,
    { loading: isSubmitting },
  ] = useMutationCreateSelfSourcedLearner({ onCompleted, onError });

  const { emailAddress, pickupPostcode } = watch();

  useEffect(() => {
    if (defaultFranchiseId) {
      // This must happen after the register of franchiseId has happened.
      setValue("franchiseId", defaultFranchiseId, true);
      setFranchiseId(defaultFranchiseId);
    }
  }, [defaultFranchiseId]);

  const { data: fullAddressData } = useQueryGetFullAddress(selectedAddressId);

  const {
    data: postcodeData,
    error,
    loading: isLoadingInstructorPrices,
  } = useQueryGetPostcodeInfo(instructorId, franchiseId, pickupPostcode);
  if (error) {
    throw error;
  }

  let defaultLessonPrice: number | undefined = undefined;
  if (!isLoadingInstructorPrices) {
    const lessonPrices = instructorPricesToLessonPrices(
      postcodeData?.instructor,
    );
    if (lessonPrices && lessonType) {
      defaultLessonPrice = lessonPrices[lessonType] || undefined;
    }
  }
  useEffect(() => {
    setLessonPrice(defaultLessonPrice?.toString());
    setValue("lessonPrice", defaultLessonPrice, true);
  }, [
    instructorId,
    pickupPostcode,
    franchiseId,
    lessonType,
    defaultLessonPrice,
  ]);

  const onLessonPriceChange = (price: string) => {
    if (price.length === 0 || isValidPrice(price)) {
      setLessonPrice(price);
      setValue("lessonPrice", parseFloat(price), false);
    }
  };

  const locations: Location[] = postcodeData?.searchForAddress || [];

  const selectedAddress: Address = fullAddressData?.getFullAddress || undefined;

  const onSubmit = (values: NewLearnerFormFields) => {
    if (!values || !values.lessonPrice) {
      return;
    }

    const address = selectedAddress || {};

    const variables = {
      ...values,
      title: values.salutation,
      addressLine1: address.line1,
      addressLine2: address.line2,
      addressTown: address.town,
      addressCounty: address.county || "",
      addressPostcode: values.pickupPostcode,
      emailAddress: values.emailAddress,
      phoneNumber: values.mobileNumber,
      learnerType: learnerLessonTypeToPersonType(values.lessonType),
      hourlyRate: values.lessonPrice,
      instructorId: instructorId,
      address: undefined,
      lineItemId: values.franchiseId,
      lessonType: values.lessonType,
      sourcedBy: UserType.Instructor,
    };

    delete variables.address;

    createLearnerMutation({
      variables,
    });
  };

  const {
    data: emailAddressExistsCheckResult,
  } = useQueryEmailAddressExistsCheck(emailAddress);

  const emailAlreadyExists =
    dirty &&
    touched.emailAddress &&
    Boolean(emailAddressExistsCheckResult?.emailAddressExistsCheck?.exists);

  if (emailAlreadyExists) {
    setError(
      "emailAddress",
      "email",
      "Email address already used. Try another",
    );
  }

  const numericPrice = parseFloat(lessonPrice || "");
  if (
    !isNaN(numericPrice) &&
    defaultLessonPrice &&
    numericPrice > defaultLessonPrice
  ) {
    setError(
      "lessonPrice",
      "exceedsMax",
      "Check that hourly rate does not exceed the default",
    );
  }

  const onFranchiseChange = (val: string) => {
    setFranchiseId(val);
    setValue("franchiseId", val, true);
    trackEvent({
      eventCategory: "NewLearner",
      eventAction: "Interaction",
      eventLabel: val,
    });
  };

  const onSalutationChange = (val: string) => {
    trackEvent({
      eventCategory: "NewLearner",
      eventAction: "Interaction",
      eventLabel: val,
    });
  };

  const handleFirstNameClick = () => {
    fireOnce("NewLearner|Interaction|First Name", () => {
      trackEvent({
        eventCategory: "NewLearner",
        eventAction: "Interaction",
        eventLabel: "First Name",
      });
    });
  };

  const handleLastNameClick = () => {
    fireOnce("NewLearner|Interaction|Last Name", () => {
      trackEvent({
        eventCategory: "NewLearner",
        eventAction: "Interaction",
        eventLabel: "Last Name",
      });
    });
  };

  const handlePostcodeClick = () => {
    fireOnce("NewLearner|Interaction|Postcode", () => {
      trackEvent({
        eventCategory: "NewLearner",
        eventAction: "Interaction",
        eventLabel: "Postcode",
      });
    });
  };

  const onAddressChange = (addressLookupResult: any) => {
    setSelectedAddressId(addressLookupResult);
    trackEvent({
      eventCategory: "NewLearner",
      eventAction: "Interaction",
      eventLabel: "Select Address",
    });
  };

  const handleMobileClick = () => {
    fireOnce("NewLearner|Interaction|Mobile", () => {
      trackEvent({
        eventCategory: "NewLearner",
        eventAction: "Interaction",
        eventLabel: "Mobile",
      });
    });
  };

  const handleEmailClick = () => {
    fireOnce("NewLearner|Interaction|Email", () => {
      trackEvent({
        eventCategory: "NewLearner",
        eventAction: "Interaction",
        eventLabel: "Email",
      });
    });
  };

  const handleDOBClick = () => {
    fireOnce("NewLearner|Interaction|DOB", () => {
      trackEvent({
        eventCategory: "NewLearner",
        eventAction: "Interaction",
        eventLabel: "DOB",
      });
    });
  };

  const onLessonChange = (val: LearnerLessonType) => {
    setLessonType(val);
    trackEvent({
      eventCategory: "NewLearner",
      eventAction: "Interaction",
      eventLabel: val,
    });
  };

  const handleSaveLearnerClick = () => {
    trackEvent({
      eventCategory: "NewLearner",
      eventAction: "Button Click",
      eventLabel: "Save New Learner",
    });
  };

  return (
    <NewLearner
      errors={errors}
      onSubmit={handleSubmit(onSubmit)}
      register={register}
      setValue={setValue}
      triggerValidation={triggerValidation}
      addresses={locations}
      loading={Boolean(isSubmitting) || instructorDetailsLoading}
      handleFirstNameClick={handleFirstNameClick}
      handleLastNameClick={handleLastNameClick}
      handlePostcodeClick={handlePostcodeClick}
      handleMobileClick={handleMobileClick}
      handleEmailClick={handleEmailClick}
      handleDOBClick={handleDOBClick}
      handleSaveLearnerClick={handleSaveLearnerClick}
      onSalutationChange={onSalutationChange}
      onLessonChange={onLessonChange}
      onAddressChange={onAddressChange}
      franchises={franchises}
      franchiseId={franchiseId}
      onFranchiseChange={onFranchiseChange}
      selectedAddressId={selectedAddressId}
      lessonType={lessonType}
      lessonPrice={lessonPrice}
      onLessonPriceChange={onLessonPriceChange}
    />
  );
};

export default NewLearnerStateProvider;
